import React from 'react';
import { ArrowDownIcon, ArrowRightIcon, LayoutTemplateIcon, SearchIcon, MessagesSquareIcon, ShoppingCartIcon, WorkflowIcon, ServerCogIcon } from 'lucide-react';
import { businessNeeds, systemCapabilities } from '../data/homepage';
const icons = {
  website: LayoutTemplateIcon,
  seo: SearchIcon,
  leads: MessagesSquareIcon,
  ecommerce: ShoppingCartIcon,
  integrations: WorkflowIcon,
  hosting: ServerCogIcon
} as const;
export function ServicesSection() {
  return <section id="services" aria-labelledby="services-title" className="bg-paper" data-id="element-126" data-mp="2bb0bxvgt527c">
      <div className="mx-auto max-w-[1280px] px-5 py-12 md:px-8 md:py-16 lg:py-20" data-id="element-127" data-mp="1xju3vazgpw8u">
        <p className="flex items-center gap-2 text-[13px] font-extrabold uppercase leading-5 text-brand-greenDeep" data-id="element-128" data-mp="1xhr624s2iytk">
          <span className="h-1.5 w-1.5 shrink-0 rotate-45 bg-brand-orange" aria-hidden data-id="element-129" data-mp="1nlo00nu3ig1c" />
          One connected system
        </p>
        <h2 id="services-title" className="mt-3 max-w-[720px] text-[30px] font-extrabold leading-[1.12] text-ink md:text-[42px]" data-id="element-130" data-mp="1sl0r5ks0299q">
          Bring in business. Keep it moving.
        </h2>
        <p className="mt-4 max-w-[640px] text-[17px] leading-[1.6] text-ink/80" data-id="element-131" data-mp="2knevpa5jsy7j">
          We connect your website, marketing, and business tools around how you work. One team builds it, keeps it
          running, and improves it with you.
        </p>

        <div className="mt-10 grid md:mt-12 lg:grid-cols-[auto_minmax(0,1fr)]" data-id="element-132" data-mp="26wjv0l57dnf1">
          {/* Business needs (inputs) + desktop connector, vertically centred against the system area */}
          <div className="lg:self-center" data-id="element-133" data-mp="21vvvtc6tm09q">
            <h3 className="text-[18px] font-bold leading-snug text-ink" data-id="element-134" data-mp="2fq57tyxt4ult">What your business needs</h3>
            <div className="mt-3 flex" data-id="element-135" data-mp="2axc2b8uili8o">
              <ul className="w-full border-t border-rule lg:w-[260px] xl:w-[280px]" data-id="element-136" data-mp="2awajgi79brge">
                {businessNeeds.map(need => <li key={need} className="flex items-center justify-between gap-3 border-b border-rule py-3 text-[16px] font-semibold leading-7 text-ink" data-id="element-137" data-mp="1j7qwcgx751gq">
                    <span className="flex items-center gap-3" data-id="element-138" data-mp="3na0jllvhv955">
                      <span className="h-1.5 w-1.5 shrink-0 rotate-45 bg-brand-green" aria-hidden data-id="element-139" data-mp="3s1s9rvaqncur" />
                      {need}
                    </span>
                    {/* Desktop: short tick feeding the collector line */}
                    <span className="hidden h-px w-5 shrink-0 bg-ink/30 lg:block" aria-hidden data-id="element-140" data-mp="2zdwemv0tht03" />
                  </li>)}
              </ul>

              {/* Desktop connector: a collector line joining the five rows (row centres ≈ 27px from
                  each end), then one arrow into the system area. Pure CSS lines, no animation. */}
              <div className="relative hidden w-16 shrink-0 lg:block xl:w-[72px]" aria-hidden data-id="element-141" data-mp="3e812ktkhc527">
                <span className="absolute bottom-[27px] left-0 top-[27px] w-px bg-ink/30" data-id="element-142" data-mp="onefvcy8gfy7" />
                <span className="absolute left-0 right-0 top-1/2 flex -translate-y-1/2 items-center" data-id="element-143" data-mp="tm97n4e6heug">
                  <span className="h-px flex-1 bg-ink/30" data-id="element-144" data-mp="2e4a35g23p9tm" />
                  <ArrowRightIcon className="-ml-1.5 h-4 w-4 shrink-0 text-brand-orange" strokeWidth={2.5} data-id="element-145" data-mp="294f9j4qxy64v" />
                </span>
              </div>
            </div>
          </div>

          {/* Mobile/tablet: restrained downward flow cue */}
          <div className="flex justify-center py-5 lg:hidden" aria-hidden data-id="element-146" data-mp="1zbgilg7z23i3">
            <span className="flex flex-col items-center" data-id="element-147" data-mp="1ufj3teccx57f">
              <span className="h-6 w-px bg-ink/30" data-id="element-148" data-mp="1qcczwdcpvq44" />
              <ArrowDownIcon className="-mt-1 h-4 w-4 text-brand-orange" strokeWidth={2.5} data-id="element-149" data-mp="1qephgpekbygn" />
            </span>
          </div>

          {/* The system: platform + team capabilities */}
          <div className="lg:border-l lg:border-rule lg:pl-10 xl:pl-12" data-id="element-150" data-mp="1gg9mgbcf051v">
            <p className="text-[12px] font-extrabold uppercase leading-5 text-brand-greenDeep" data-id="element-151" data-mp="3fqhqgmsjdziu">
              WebsiteForge platform + team
            </p>
            <h3 className="mt-1.5 text-[22px] font-extrabold leading-tight text-ink md:text-[26px]" data-id="element-152" data-mp="28f1xvnigi9z5">
              Built around you. Managed together.
            </h3>
            <ul className="mt-5 grid border-t border-rule sm:grid-cols-2 sm:gap-x-10" data-id="element-153" data-mp="2cfu7ihv8qgvy">
              {systemCapabilities.map(cap => {
              const Icon = icons[cap.id as keyof typeof icons];
              return <li key={cap.id} className="flex gap-4 border-b border-rule py-5" data-id="element-154" data-mp="1or2nwevpv6us">
                    <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-mint text-brand-greenDeep" data-id="element-155" data-mp="1op810v0vx4o2">
                      <Icon className="h-5 w-5" strokeWidth={1.75} aria-hidden data-id="element-156" data-mp="1fr5szviqhbra" />
                    </span>
                    <div className="min-w-0" data-id="element-157" data-mp="1kqiqda78e184">
                      <h4 className="text-[17px] font-bold leading-snug text-ink" data-id="element-158" data-mp="1kncxdjvhef74">{cap.title}</h4>
                      <p className="mt-1 text-[16px] leading-[1.55] text-ink/80" data-id="element-159" data-mp="15u9qq7jfhlv6">{cap.body}</p>
                    </div>
                  </li>;
            })}
            </ul>
          </div>
        </div>

        <div className="mt-10 flex flex-col gap-4 border-t border-rule pt-6 sm:flex-row sm:items-center sm:justify-between sm:gap-8" data-id="element-160" data-mp="n3wel8lotmwo">
          <p className="text-[20px] font-extrabold leading-snug text-ink md:text-[22px]" data-id="element-161" data-mp="114p75nsfi7x5">
            One coordinated system. One accountable team.
          </p>
          <a href="#contact" className="group inline-flex min-h-[44px] shrink-0 items-center gap-2 self-start whitespace-nowrap text-[16px] font-bold text-ink underline decoration-brand-orange decoration-2 underline-offset-[6px] transition-colors duration-150 ease-out hover:decoration-ink sm:self-auto" data-id="element-162" data-mp="1a4caif4pxnsu">
            Talk through what you need
            <ArrowRightIcon className="h-4 w-4 transition-transform duration-150 ease-out group-hover:translate-x-0.5" aria-hidden data-id="element-163" data-mp="1ex5daxgmmm2a" />
          </a>
        </div>
      </div>
    </section>;
}